import dayjs from 'dayjs'
import { capitalize, humanize } from 'inflection'
import { getAgentTimezone } from 'v2/pages/conversations/utils'
import type { ConversationMetrics } from '../../types'
import type { StatKey, StatsConfig } from './types'

export const getStatValue = (key: StatKey, metrics?: ConversationMetrics): unknown => {
  if (!metrics) return undefined
  return metrics[key]
}

/**
 * Whether the stats table is worth showing. Chat carries far fewer of these
 * metrics than voice, so a chat run would otherwise render a table where every
 * row reads N/A. Voice is deliberately excluded from that rule — it shows an
 * all-N/A table today and must keep doing so.
 */
export const shouldRenderStats = (
  isChat: boolean,
  stats: StatsConfig[],
  metrics?: ConversationMetrics,
): boolean => {
  if (!isChat) return true
  return stats.some((stat) => getStatValue(stat.key, metrics) != null)
}

export const formatStatValue = (key: StatKey, value: unknown): string => {
  if (value === null || value === undefined) return 'N/A'

  if (key === 'answered') return value ? 'Answered' : 'Not Answered'

  if (typeof value === 'boolean') return value ? 'Yes' : 'No'

  if (typeof value === 'string') return capitalize(humanize(value))

  return String(value)
}

export const getValueClass = (key: StatKey, value: unknown): string => {
  if (value === null || value === undefined) return 'text-primary'

  switch (key) {
    case 'answered':
      return value ? 'text-primary' : 'text-status-danger'
    default:
      return 'text-primary'
  }
}

/**
 * React key for one row of a node's transcript. Chat emits several rows per turn
 * (a message plus its card) that can share a timestamp, so the position is part
 * of the key — a timestamp alone is not unique.
 */
export const getChunkRowKey = (timestamp: string, index: number): string => `${timestamp}-${index}`

export const getFormattedTime = (time: string) => {
  const agentTimezone = getAgentTimezone()
  return dayjs.utc(time).tz(agentTimezone).format('HH:mm:ss')
}

/**
 * Clock time with the milliseconds kept legible but de-emphasised. Several events
 * routinely share one second (a commit, its metrics and its transcript land together),
 * and at second precision their order and spacing are unreadable.
 */
export const TimeWithMillis = ({ time }: { time: string }) => (
  <>
    {getFormattedTime(time)}
    <span className="text-xxs-400 opacity-60">
      .{dayjs.utc(time).tz(getAgentTimezone()).format('SSS')}
    </span>
  </>
)
